/**
 * @file src/components/DownloadModal.tsx
 * Modal for PDF download with optional filename input
 */

import { useEffect, useRef } from 'react'
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  TextField,
  Box
} from '@mui/material'

interface DownloadModalProps {
  open: boolean
  onClose: () => void
  onDownload: (filename?: string) => void
}

export const DownloadModal = ({ open, onClose, onDownload }: DownloadModalProps) => {
  const inputRef = useRef<HTMLInputElement>(null)
  
  useEffect(() => {
    if (open) {
      // Use a slightly longer delay to ensure the modal is fully rendered
      const timer = setTimeout(() => {
        inputRef.current?.focus()
      }, 200)
      return () => clearTimeout(timer)
    }
  }, [open])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const filename = inputRef.current?.value
    onDownload(filename || undefined)
    onClose()
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmit(e)
    }
  }

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      TransitionProps={{
        onEntered: () => {
          // Focus immediately after transition ends
          inputRef.current?.focus()
        }
      }}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle>Download PDF</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              inputRef={inputRef}
              fullWidth
              label="Filename (optional)"
              placeholder="Enter filename for your PDF"
              variant="outlined"
              onKeyDown={handleKeyDown}
              autoFocus
              InputProps={{
                endAdornment: '.pdf'
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="inherit">
            Cancel
          </Button>
          <Button type="submit" variant="contained" color="primary">
            Download
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
} 